import { CurrencyCode } from 'further-types/lp/vehicle';
import Table from 'further-ui/components/Table';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import { sumBy } from 'lodash';

type Props = {
  investorsBreakdown: Array<{
    investorName: string;
    commitmentId: string;
    investments: number;
    expenses: number;
    fees: number;
    workingCapital: number;
    advanceSubscriptionCalled: number;
    distributions: number;
    distributionsOutsideOfCarry: number;
    returnOfCapital: number;
    equalisations: number;
    equalisationInterest: number;
    netCallOrDistribution: number;
    capitalContributionToCall: number;
    capitalContributionUsed: number;
    advanceSubscriptionUsed: number;
    totalToCall: number;
    partnerTransfersToCall: number;
  }>;
  baseCurrency?: CurrencyCode;
};

const InvestorBreakdown: React.FC<Props> = ({
  baseCurrency,
  investorsBreakdown,
}) => {
  const investmentsTotal = sumBy(investorsBreakdown, 'investments');
  const expensesTotal = sumBy(investorsBreakdown, 'expenses');
  const feesTotal = sumBy(investorsBreakdown, 'fees');
  const workingCapitalTotal = sumBy(investorsBreakdown, 'workingCapital');
  const advanceSubscriptionCalledTotal = sumBy(
    investorsBreakdown,
    'advanceSubscriptionCalled',
  );
  const equalisationsTotal = sumBy(investorsBreakdown, 'equalisations');
  const equalisationInterestTotal = sumBy(
    investorsBreakdown,
    'equalisationInterest',
  );
  const distributionsTotal = sumBy(investorsBreakdown, 'distributions');
  const distributionsOutsideOfCarryTotal = sumBy(
    investorsBreakdown,
    'distributionsOutsideOfCarry',
  );
  const returnOfCapitalTotal = sumBy(investorsBreakdown, 'returnOfCapital');
  const capitalContributionUsedTotal = sumBy(
    investorsBreakdown,
    'capitalContributionUsed',
  );
  const capitalContributionToCallTotal = sumBy(
    investorsBreakdown,
    'capitalContributionToCall',
  );
  const advanceSubscriptionUsedTotal = sumBy(
    investorsBreakdown,
    'advanceSubscriptionUsed',
  );
  const netCallOrDistributionTotal = sumBy(
    investorsBreakdown,
    'netCallOrDistribution',
  );
  const adjustedTotalCallTotal = sumBy(
    investorsBreakdown,
    (e) => e.totalToCall - e.advanceSubscriptionUsed,
  );
  const partnerTransfersToCallTotal = sumBy(
    investorsBreakdown,
    (b) => b.partnerTransfersToCall,
  );

  const hasInvestments = investmentsTotal !== 0;
  const hasExpenses = expensesTotal !== 0;
  const hasFees = investorsBreakdown.some((b) => b.fees);
  const hasWorkingCapital = workingCapitalTotal !== 0;
  const hasAdvanceSubscriptionCalled = advanceSubscriptionCalledTotal !== 0;
  const hasEqualisations = equalisationsTotal !== 0;
  const hasEqualisationsInterest = investorsBreakdown.some(
    (b) => b.equalisationInterest,
  );
  const hasDistributions = distributionsTotal !== 0;
  const hasDistributionsOutsideOfCarry = distributionsOutsideOfCarryTotal !== 0;
  const hasReturnOfCapital = investorsBreakdown.some((b) => b.returnOfCapital);
  const hasCapitalContributionUsed = investorsBreakdown.some(
    (b) => b.capitalContributionUsed,
  );
  const hasCapitalContributionToCall = investorsBreakdown.some(
    (b) => b.capitalContributionToCall,
  );
  const hasAdvanceSubscriptionUsed = investorsBreakdown.some(
    (b) => b.advanceSubscriptionUsed,
  );
  const hasPartnerTransfers = investorsBreakdown.some(
    (b) => b.partnerTransfersToCall,
  );

  const rows = investorsBreakdown.map((entry) => ({
    ...entry,
    id: entry.commitmentId,
  }));

  const callIsEmpty =
    !hasInvestments &&
    !hasExpenses &&
    !hasFees &&
    !hasWorkingCapital &&
    !hasAdvanceSubscriptionCalled &&
    !hasEqualisations &&
    !hasEqualisationsInterest &&
    !hasDistributions &&
    !hasDistributionsOutsideOfCarry &&
    !hasReturnOfCapital &&
    !hasCapitalContributionToCall &&
    !hasCapitalContributionUsed &&
    !hasPartnerTransfers &&
    !hasAdvanceSubscriptionUsed;

  const showAdjustedTotalCall =
    hasCapitalContributionUsed ||
    hasCapitalContributionToCall ||
    hasAdvanceSubscriptionUsed;

  const currency = (value: number, negativeInBrackets = false) =>
    numberToCurrencyString(value, {
      currency: baseCurrency,
      negativeInBrackets,
    });

  const columns = [
    {
      key: 'investorName',
      label: 'Investor',
      colspan: callIsEmpty ? 2 : 1,
      width: 250,
      footer: `Total from ${investorsBreakdown.length} ${pluralize(
        'investor',
        investorsBreakdown.length,
      )}`,
    },
    {
      key: 'netCallOrDistribution',
      label: 'Net call/(distribution)',
      withVerticalBorder: !showAdjustedTotalCall,
      width: 200,
      footer: currency(netCallOrDistributionTotal, true),
      render: (row: (typeof rows)[number]) =>
        currency(row.netCallOrDistribution, true),
    },
    ...(showAdjustedTotalCall
      ? [
          {
            key: 'totalToCall',
            label: 'Adjusted total call',
            withVerticalBorder: true,
            width: 200,
            footer: currency(adjustedTotalCallTotal, true),
            render: (row: (typeof rows)[number]) =>
              currency(row.totalToCall - row.advanceSubscriptionUsed, true),
          },
        ]
      : []),
    ...(hasInvestments
      ? [
          {
            key: 'investments',
            width: 200,
            label: 'Investment call',
            footer: currency(investmentsTotal),
            render: (row: (typeof rows)[number]) => currency(row.investments),
          },
        ]
      : []),
    ...(hasExpenses
      ? [
          {
            key: 'expenses',
            width: 200,
            label: 'Expense call',
            footer: currency(expensesTotal),
            render: (row: (typeof rows)[number]) => currency(row.expenses),
          },
        ]
      : []),
    ...(hasFees
      ? [
          {
            key: 'fees',
            width: 200,
            label: 'Management fee call',
            footer: currency(feesTotal),
            render: (row: (typeof rows)[number]) => currency(row.fees),
          },
        ]
      : []),
    ...(hasWorkingCapital
      ? [
          {
            key: 'workingCapital',
            label: 'General capital call',
            width: 200,
            footer: currency(workingCapitalTotal),
            render: (row: (typeof rows)[number]) =>
              currency(row.workingCapital),
          },
        ]
      : []),
    ...(hasEqualisations
      ? [
          {
            key: 'equalisations',
            width: 200,
            label: 'Equalisation call / (distribution)',
            footer: currency(equalisationsTotal, true),
            render: (row: (typeof rows)[number]) =>
              currency(row.equalisations, true),
          },
        ]
      : []),
    ...(hasPartnerTransfers
      ? [
          {
            key: 'partnerTransfersToCall',
            width: 200,
            label: 'Partner transfers',
            render: (row: (typeof rows)[number]) =>
              currency(row.partnerTransfersToCall, true),
            footer: currency(partnerTransfersToCallTotal),
          },
        ]
      : []),
    ...(hasEqualisationsInterest
      ? [
          {
            key: 'equalisationInterest',
            label: 'Equalisation interest paid/(received)',
            width: 200,
            footer: currency(equalisationInterestTotal, true),
            render: (row: (typeof rows)[number]) =>
              numberToCurrencyString(row.equalisationInterest, {
                currency: baseCurrency,
                negativeInBrackets: true,
              }),
          },
        ]
      : []),
    ...(hasDistributions
      ? [
          {
            key: 'distributions',
            width: 200,
            label: 'Distributions',
            footer: currency(distributionsTotal),
            render: (row: (typeof rows)[number]) => currency(row.distributions),
          },
        ]
      : []),
    ...(hasDistributionsOutsideOfCarry
      ? [
          {
            key: 'distributionsOutsideOfCarry',
            width: 200,
            label: 'Distributions outside of carry',
            footer: currency(distributionsOutsideOfCarryTotal),
            render: (row: (typeof rows)[number]) =>
              currency(row.distributionsOutsideOfCarry),
          },
        ]
      : []),
    ...(hasReturnOfCapital
      ? [
          {
            key: 'returnOfCapital',
            width: 200,
            label: 'Return of capital',
            footer: currency(returnOfCapitalTotal),
            render: (row: (typeof rows)[number]) =>
              currency(row.returnOfCapital),
          },
        ]
      : []),
    ...(hasAdvanceSubscriptionCalled
      ? [
          {
            key: 'advanceSubscriptionCalled',
            width: 200,
            label: 'Advance subscription called',
            footer: currency(advanceSubscriptionCalledTotal),
            render: (row: (typeof rows)[number]) =>
              currency(row.advanceSubscriptionCalled),
          },
        ]
      : []),
    ...(hasAdvanceSubscriptionUsed
      ? [
          {
            key: 'advanceSubscriptionUsed',
            width: 200,
            label: 'Advance subscription used',
            footer: `(${currency(advanceSubscriptionUsedTotal)})`,
            render: (row: (typeof rows)[number]) =>
              `(${currency(row.advanceSubscriptionUsed)})`,
          },
        ]
      : []),
    ...(hasCapitalContributionToCall
      ? [
          {
            key: 'capitalContributionToCall',
            width: 200,
            label: 'Capital contribution',
            footer: currency(capitalContributionToCallTotal),
            render: (row: (typeof rows)[number]) =>
              currency(row.capitalContributionToCall),
          },
        ]
      : []),
    ...(hasCapitalContributionUsed
      ? [
          {
            key: 'capitalContributionUsed',
            label: 'Capital contribution used',
            width: 200,
            footer: `(${currency(capitalContributionUsedTotal)})`,
            render: (row: (typeof rows)[number]) =>
              `(${currency(row.capitalContributionUsed)})`,
          },
        ]
      : []),
  ];

  return <Table scrollable columns={columns} rows={rows} />;
};

export default InvestorBreakdown;
